import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

import styled from 'styled-components';

import { CarStore, AppStore } from 'stores';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Input, Tooltip, message, Row, Col } from 'antd';
import { generateRandomId } from 'containers/InspectionForms/common/utils';

const InputStyled = styled(Input)`
  && {
    .ant-input {
      padding-right: 50px;
    }

    .ant-input-suffix {
      i {
        padding: 0px 2px;
      }
    }
  }
`;

const TextRow = styled.div`
  display: flex;
  margin-top: 4px;
  margin-bottom: 4px;
  height: 34px;
`;

const LabelText = styled.p`
  width: 40%;
  font-weight: 700;
  margin-bottom: 0;
  align-self: center;
`;

const TextStlyed = styled.div`
  // flex: 1;
  margin-bottom: 0;
  align-self: center;
`;

const EditableRow = styled(Row)`
  margin-bottom: 5px;
`;

function CustomFileds({ carId, customFileds = {} }) {
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (customFileds?.length) {
      setFields(customFileds);
    }
  }, [customFileds]);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { id: `tmp-${generateRandomId()}`, title: '', value: '' },
    ]);
  };

  const updateField = (id, data) => {
    setFields((prev) =>
      prev.map((field) => (field.id === id ? { ...field, ...data } : field))
    );
  };

  const deleteField = (id) => {
    setFields((prev) => prev.filter((field) => field.id !== id));
  };

  const saveFieldToServer = async (field) => {
    try {
      await CarStore.saveDetails(carId, field);
      return field;
    } catch (e) {
      throw Error(e);
    }
  };

  const deleteFieldFromServer = async (id) => {
    try {
      await CarStore.deleteDetails(carId, id);
      return id;
    } catch (e) {
      throw Error(e);
    }
  };

  return (
    <>
      {fields.map((field) => (
        <RenderField
          key={field.id}
          field={field}
          updateField={updateField}
          deleteField={deleteField}
          saveFieldToServer={saveFieldToServer}
          deleteFieldFromServer={deleteFieldFromServer}
          setFields={setFields}
          fields={fields}
        />
      ))}
      <Button
        type="link"
        icon={<PlusCircleOutlined />}
        style={{ marginTop: 10, paddingLeft: 0 }}
        onClick={addField}
      >
        Add New Field
      </Button>
    </>
  );
}

export default CustomFileds;

function RenderField({
  field,
  updateField,
  deleteField,
  saveFieldToServer,
  deleteFieldFromServer,
  setFields,
  fields,
}) {
  const [editable, setEditable] = useState(field.id.startsWith('tmp-'));
  const [localTitle, setLocalTitle] = useState(field.title);
  const [localValue, setLocalValue] = useState(field.value);

  useEffect(() => {
    setLocalTitle(field.title);
    setLocalValue(field.value);
  }, [field.title, field.value]);

  const onTitleChange = (e) => setLocalTitle(e.target.value);
  const onValueChange = (e) => setLocalValue(e.target.value);

  const onConfirm = async () => {
    if (!localTitle) {
      AppStore.addError('Title required');
      return;
    }

    let newField = { ...field, title: localTitle, value: localValue };

    try {
      if (field.id.startsWith('tmp-')) {
        const cleanId = field.id.replace(/^tmp-/, '');
        newField.id = cleanId;

        setFields((fields) =>
          fields.map((f) => (f.id === field.id ? { ...newField } : f))
        );

        await saveFieldToServer({ ...newField });
        setEditable(false);
        AppStore.addSuccess('Update data successful');
      } else {
        setFields((fields) =>
          fields.map((f) =>
            f.id === field.id
              ? { ...f, title: localTitle, value: localValue }
              : f
          )
        );
        await saveFieldToServer(newField);
        AppStore.addSuccess('Update data successful');
        setEditable(false);
      }
    } catch (e) {
      console.log('Error: ', e);
      AppStore.addError('Something went wrong');
      setEditable(true);
    }
  };

  const onDelete = async () => {
    try {
      if (!field.id.startsWith('tmp-')) {
        await deleteFieldFromServer(field.id);
      }
      deleteField(field.id);
      AppStore.addSuccess('Deletion was successful');
    } catch (e) {
      console.log('Error: ', e);
      AppStore.addError('Something went wrong');
    }
  };

  const onClose = () => {
    if (!field.id.startsWith('tmp-')) {
      setEditable(false);
      setLocalTitle(field.title);
      setLocalValue(field.value);
    } else {
      deleteField(field.id);
    }
  };

  if (editable) {
    return (
      <EditableRow gutter={[16, 5]}>
        <Col span={9}>
          <InputStyled
            required
            value={localTitle}
            onChange={onTitleChange}
            placeholder="Title"
          />
        </Col>
        <Col span={15}>
          <InputStyled
            required
            suffix={[
              <Tooltip key="close-circle" title="Close">
                <CloseCircleOutlined onClick={onClose} />
              </Tooltip>,
              <Tooltip key="check-circle" title="Confirm">
                <CheckCircleOutlined onClick={onConfirm} />
              </Tooltip>,
            ]}
            value={localValue}
            onChange={onValueChange}
          />
        </Col>
      </EditableRow>
    );
  }

  return (
    <TextRow>
      <LabelText>{field.title}</LabelText>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '60%',
        }}
      >
        <TextStlyed>{field.value}</TextStlyed>
        <div style={{ paddingRight: '15px' }}>
          <Tooltip title="Edit">
            <EditOutlined onClick={() => setEditable(true)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined onClick={onDelete} />
          </Tooltip>
        </div>
      </div>
    </TextRow>
  );
}
